import {
  ITeroOrderAdditionalInfo,
  ITeroOrderAddress,
  ITeroOrderPayment,
  OrderStep,
} from 'components';
import { FC, useEffect, useState } from 'react';
import {
  getOrderStepLabel,
  getOrderStepModes,
  ITeroCheckoutStates,
  IteroOrderErrorModal,
  IteroOrderLoadingModal,
  IteroOrderSuccessModal,
  Styled,
} from '.';

import {
  useAccountDetails,
  useDoctorProfile,
  useRetriveCart,
} from 'reactQuery/queries';
import {
  Address,
  CreditCardClientData,
  IteroProposalClient,
  ScannerSubFundingSource,
} from 'types';

import { IteroOrderAdditionalInfo } from 'components/ITeroOrderAdditionalInfo';
import { ITeroProposalSummary } from 'components/ITeroProposal';
import ITeroTermsAndConditions from 'components/ITeroTermsAndConditions/ITeroTermsAndConditions';
import { useRouter } from 'next/router';
import { usePlaceIteroOrder } from 'reactQuery/mutations/itero/usePlaceIteroOrder';
import { getSubFundingSourceForSalesforce } from 'components/ITeroOrderPayment/ITeroOrderPayment.helper';
import { useCalculateIteroTax } from 'reactQuery/queries/iTero/useCalculateIteroTax';
import handleITeroCustomerNeeds from 'utils/handleITeroCustomerNeeds';
import { RentalProposalSummary } from 'components/ITeroProposal/RentalProposalSummary';
import { isEMEAStore } from 'utils';

const ITeroCheckout: FC<{
  ipAddress: string;
  currentProposal: IteroProposalClient;
}> = ({ currentProposal, ipAddress }) => {
  const router = useRouter();
  const { data: doctorProfile } = useDoctorProfile();
  const { data: accountDetails } = useAccountDetails();

  const [orderStep, setOrderStep] = useState<ITeroCheckoutStates>('address');
  const [orderFailureMessage, setOrderFailureMessage] = useState('');

  const [selectedShippingAddress, setShippingAddress] = useState<Address>();

  const [selectedBillingAddress, setBillingAddress] = useState<Address>();

  const saleType = currentProposal.saleType;
  const tmSelectedScannerFundingSource = currentProposal.scannerFundingSource;
  const tmSelectedScannerSubFundingSource =
    currentProposal.scannerSubFundingSource;
  const [selectedScannerSubFundingSource, setSelectedScannerSubFundingSource] =
    useState<ScannerSubFundingSource>(tmSelectedScannerSubFundingSource);

  const [
    selectedScannerPaymentCreditCard,
    setSelectedScannerPaymentCreditCard,
  ] = useState<CreditCardClientData | undefined>();
  const [
    selectedSubscriptionPaymentCreditCard,
    setSelectedSubscriptionPaymentCreditCard,
  ] = useState<CreditCardClientData | undefined>();

  const [selectedRentalPaymentCreditCard, setSelectedRentalPaymentCreditCard] =
    useState<CreditCardClientData | undefined>();

  const isEU = isEMEAStore();

  const [additionalOrderInfo, setAdditionalOrderInfo] =
    useState<IteroOrderAdditionalInfo>({
      doctorName:
        doctorProfile?.firstName + ' ' + doctorProfile?.lastName || '',
      // include license fields only for non-EU doctors
      ...(!isEU && {
        licenceNumber: accountDetails?.licenseNumber || '',
        stateOfIssuance: accountDetails?.licenseState || '',
      }),
      contactEmail: accountDetails?.email || '',
      scannerLoginEmail: accountDetails?.email || '',
      doctorPhone: '',
      firstName: '',
      lastName: '',
      directPhone: '',
      directEmail: '',
      ...(isEU && {
        deliveryDate: '',
      }),
    });

  const financingCompany = currentProposal.financingCompany;
  const existingScannerSerialNumber =
    currentProposal.existingScannerSerialNumber;

  useEffect(() => {
    setAdditionalOrderInfo({
      ...additionalOrderInfo,
      // include license fields only for non-EU doctors
      ...(!isEU && {
        licenceNumber: accountDetails?.licenseNumber || '',
        stateOfIssuance: accountDetails?.licenseState || '',
      }),
      doctorPhone: accountDetails?.phone || '',
      contactEmail: accountDetails?.email || '',
      scannerLoginEmail: accountDetails?.email || '',
    });
  }, [accountDetails]);

  const placeIteroOrderMutation = usePlaceIteroOrder();

  const { data: iTeroTax } = useCalculateIteroTax(
    currentProposal.proposalId,
    selectedBillingAddress?.salesforceId || '',
    selectedShippingAddress?.salesforceId || '',
    orderStep,
    saleType
  );

  const orderStepModes = getOrderStepModes(orderStep);

  const { isRental } = handleITeroCustomerNeeds(currentProposal);

  const handleAddressChange = () => {
    if (isRental) {
      setOrderStep('rentalPayment');
    } else {
      setOrderStep('scannerPayment');
    }
  };

  const handleScannerPaymentMethod = () => {
    setOrderStep('subscriptionPayment');
  };

  const handleSubscriptionPaymentMethod = () => {
    setOrderStep('additionalInfo');
  };

  const handleRentalPaymentMethod = () => {
    setOrderStep('additionalInfo');
  };

  const handleTermsAndConditions = () => {
    setOrderStep('termsAndConditions');
  };

  const handlePlaceIteroOrder = (base64Stringpdf: string) => {
    const salesforceSubfundingSource = getSubFundingSourceForSalesforce(
      tmSelectedScannerFundingSource,
      selectedScannerSubFundingSource
    );
    placeIteroOrderMutation.mutate({
      selectedProposalId: currentProposal.proposalId,
      selectedShippingAddress,
      selectedBillingAddress,
      selectedScannerSubFundingSource: salesforceSubfundingSource,
      selectedScannerPaymentCreditCard,
      selectedSubscriptionPaymentCreditCard,
      selectedRentalPaymentCreditCard,
      additionalOrderInfo,
      setOrderStep: setOrderStep,
      setOrderFailureMessage: setOrderFailureMessage,
      ipAddress: ipAddress,
      pcUsername: doctorProfile?.pcUserName,
      base64Stringpdf: base64Stringpdf,
    });
  };

  const handleEdit = (step: ITeroCheckoutStates) => {
    setOrderStep(step);
  };

  return (
    <Styled.CheckoutContainer>
      <Styled.CheckoutWrapper>
        {placeIteroOrderMutation.isLoading && <IteroOrderLoadingModal />}
        {orderStep === 'failed' && (
          <IteroOrderErrorModal
            onClose={() => {
              setOrderStep('termsAndConditions');
            }}
          />
        )}
        {orderStep === 'complete' && (
          <IteroOrderSuccessModal
            onClose={() => {
              router.push(`/orderhistorylog?selected=iteroorders`);
            }}
          />
        )}

        <Styled.CheckoutStepsContainer>
          <OrderStep
            title={getOrderStepLabel('address')}
            mode={orderStepModes.address}
            onEdit={() => handleEdit('address')}
          >
            <ITeroOrderAddress
              mode={orderStepModes.address}
              selectedShippingAddress={selectedShippingAddress}
              setShippingAddress={setShippingAddress}
              selectedBillingAddress={selectedBillingAddress}
              setBillingAddress={setBillingAddress}
              onSave={handleAddressChange}
              tmSelectedBillAddressId={currentProposal.billAddressId}
              tmSelectedShipAddressId={currentProposal.shipAddressId}
            />
          </OrderStep>

          {isRental && (
            <OrderStep
              title={getOrderStepLabel('rentalPayment', isRental)}
              mode={orderStepModes.rentalPayment}
              onEdit={() => handleEdit('rentalPayment')}
            >
              <ITeroOrderPayment
                mode={orderStepModes.rentalPayment}
                onSubmit={handleRentalPaymentMethod}
                selectedCreditCard={selectedRentalPaymentCreditCard}
                setCreditCard={setSelectedRentalPaymentCreditCard}
                paymentType="rental"
                selectedScannerSubFundingSource={
                  selectedScannerSubFundingSource
                }
                setSelectedScannerSubFundingSource={
                  setSelectedScannerSubFundingSource
                }
                tmSelectedScannerFundingSource={tmSelectedScannerFundingSource}
                tmSelectedScannerSubFundingSource={
                  tmSelectedScannerSubFundingSource
                }
                financingCompany={financingCompany}
              />
            </OrderStep>
          )}

          {saleType !== 'Rental' && (
            <>
              <OrderStep
                title={getOrderStepLabel('scannerPayment', isRental)}
                mode={orderStepModes.scannerPayment}
                onEdit={() => handleEdit('scannerPayment')}
              >
                <ITeroOrderPayment
                  mode={orderStepModes.scannerPayment}
                  onSubmit={handleScannerPaymentMethod}
                  selectedCreditCard={selectedScannerPaymentCreditCard}
                  setCreditCard={setSelectedScannerPaymentCreditCard}
                  paymentType="scanner"
                  selectedScannerSubFundingSource={
                    selectedScannerSubFundingSource
                  }
                  setSelectedScannerSubFundingSource={
                    setSelectedScannerSubFundingSource
                  }
                  tmSelectedScannerFundingSource={
                    tmSelectedScannerFundingSource
                  }
                  tmSelectedScannerSubFundingSource={
                    tmSelectedScannerSubFundingSource
                  }
                  financingCompany={financingCompany}
                />
              </OrderStep>

              <OrderStep
                title={getOrderStepLabel('subscriptionPayment', isRental)}
                mode={orderStepModes.subscriptionPayment}
                onEdit={() => handleEdit('subscriptionPayment')}
              >
                <ITeroOrderPayment
                  mode={orderStepModes.subscriptionPayment}
                  onSubmit={handleSubscriptionPaymentMethod}
                  selectedCreditCard={selectedSubscriptionPaymentCreditCard}
                  setCreditCard={setSelectedSubscriptionPaymentCreditCard}
                  paymentType="subscription"
                  selectedScannerSubFundingSource={
                    selectedScannerSubFundingSource
                  }
                  setSelectedScannerSubFundingSource={
                    setSelectedScannerSubFundingSource
                  }
                  tmSelectedScannerFundingSource={
                    tmSelectedScannerFundingSource
                  }
                  tmSelectedScannerSubFundingSource={
                    tmSelectedScannerSubFundingSource
                  }
                  financingCompany={financingCompany}
                />
              </OrderStep>
            </>
          )}

          <OrderStep
            title={getOrderStepLabel('additionalInfo', isRental)}
            mode={orderStepModes.additionalInfo}
            onEdit={() => handleEdit('additionalInfo')}
          >
            <ITeroOrderAdditionalInfo
              mode={orderStepModes.additionalInfo}
              onSave={handleTermsAndConditions}
              additionalOrderInfo={additionalOrderInfo}
              setAdditionalOrderInfo={setAdditionalOrderInfo}
              existingScannerSerialNumber={existingScannerSerialNumber ?? ''}
            />
          </OrderStep>

          <OrderStep
            title={getOrderStepLabel('termsAndConditions', isRental)}
            mode={orderStepModes.termsAndConditions}
            onEdit={() => {
              handleEdit('scannerPayment');
            }}
          >
            <ITeroTermsAndConditions
              onPlaceIteroOrder={handlePlaceIteroOrder}
              additionalOrderInfo={additionalOrderInfo}
              selectedScannerSubFundingSource={selectedScannerSubFundingSource}
              selectedShippingAddress={selectedShippingAddress}
              selectedBillingAddress={selectedBillingAddress}
              doctorProfile={doctorProfile as any} // "as any" to prevent TS error
              currentProposal={currentProposal}
              selectedCreditCard={selectedScannerPaymentCreditCard}
            />
          </OrderStep>
        </Styled.CheckoutStepsContainer>
      </Styled.CheckoutWrapper>
      <Styled.SummaryWrapper>
        {isRental ? (
          <RentalProposalSummary
            currentProposal={currentProposal}
            setProceedToCheckout={() => ({})}
            orderStep={orderStep}
          />
        ) : (
          <ITeroProposalSummary
            orderStep={orderStep}
            currentProposal={currentProposal}
            setProceedToCheckout={() => ({})}
            displayTax={iTeroTax?.displayTax}
            displayTotalAfterTax={iTeroTax?.displayTotalAfterTax}
            estimatedMonthlyPayment={iTeroTax?.estimatedMonthlyPayment}
          />
        )}
      </Styled.SummaryWrapper>
    </Styled.CheckoutContainer>
  );
};

export default ITeroCheckout;
